import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import ChargeManagementService, {
  DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE,
  SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE,
} from "services/chargeManagementService";
import EventBus, {
  downloadPaymentConfirmationFailure,
  emailUpdated,
  putChargeContactSuccess,
  sendChargePaymentConfirmationEmailFailure,
  sendChargePaymentConfirmationEmailModalClosed,
  sendChargePaymentConfirmationEmailSuccess,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import ChargeManagementEditPartViewModel from "./chargeManagementEditPartViewModel";
import React from "react";
import Reaptcha from "reaptcha";
import { fixReCaptchaChallengePosition } from "../../../utils/recaptchaUtils";
import * as Sentry from "@sentry/react";
import { validate } from "@frui.ts/validation";
import { saveFileFromURL } from "../../../utils/fileUtils";
import NotificationPreferences from "models/notificationPreferences";
import ReceiptEmailModalViewModel from "./receiptEmailModalViewModel";
import EnumService from "services/enumService";
import ChargeTypesService from "services/chargeTypesService";

export default class ChargeManagementReceiptViewModel extends ChargeManagementEditPartViewModel {
  EMAIL_REGEX = "%email%";
  recaptchaRef = React.createRef<Reaptcha>();
  recaptchaAction?: () => Promise<void>;
  recatpchaFailed?: () => void;
  recaptchaToken: string;
  @observable emailInputViewModel?: ReceiptEmailModalViewModel;
  @observable showModal = false;
  @observable emailModalOpened = false;
  @observable checkRequiredEmail = false;
  @observable private notificationPreferences = new NotificationPreferences();

  constructor(
    public localizationService: LocalizationService,
    protected chargeManagementService: ChargeManagementService,
    protected chargeTypesService: ChargeTypesService,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected signalRNotificationService: SignalRNotificationService,
    private receiptEmailModalViewModelFactory: ReturnType<typeof ReceiptEmailModalViewModel.Factory>
  ) {
    super();
    this.navigationName = "receipt";
    this.unsubscriptions.push(
      this.eventBus.subscribe(downloadPaymentConfirmationFailure, this.downloadPaymentConfirmationFailure),
      this.eventBus.subscribe(sendChargePaymentConfirmationEmailModalClosed, this.sendChargePaymentConfirmationEmailModalClosed),
      this.eventBus.subscribe(sendChargePaymentConfirmationEmailFailure, this.sendChargePaymentConfirmationEmailFailure),
      this.eventBus.subscribe(sendChargePaymentConfirmationEmailSuccess, this.sendChargePaymentConfirmationEmailSuccess),
      this.eventBus.subscribe(putChargeContactSuccess, this.putChargeContactSuccess),
      this.eventBus.subscribe(emailUpdated, this.onEmailUpdated)
    );
  }

  protected onInitialize(): Promise<any> | void {
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach((unsubscribe) => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  get canEdit(): boolean {
    return true;
  }

  protected validate(): boolean {
    return validate(this.notificationPreferences);
  }

  submit() {
    // silence is golden
  }

  @bound
  protected openChargePaymentConfirmationModal() {
    this.showModal = true;
    if (this.charge) {
      this.emailModalOpened = true;
      this.emailInputViewModel = this.receiptEmailModalViewModelFactory(
        this.charge,
        this.closeEmailModal,
        this.sendPaymentConfirmationEmail
      );
    }
  }

  @bound
  protected downloadPaymentConfirmationFailure(data: { type: string; payload: { type: string } }) {
    this.success = false;
    this.errorMessage = this.genericError;
  }

  @bound
  protected sendChargePaymentConfirmationEmailModalClosed() {
    this.emailModalOpened = false;
  }

  @bound
  protected sendChargePaymentConfirmationEmailSuccess() {
    this.success = true;
    this.emailModalOpened = false;
  }

  @bound
  protected sendChargePaymentConfirmationEmailFailure(data: ReturnType<typeof sendChargePaymentConfirmationEmailFailure>) {
    this.success = false;
    this.errorMessage = this.genericError;
  }

  @computed
  get customSuccessMessage() {
    return this.translate("receipt.success").replace(
      this.EMAIL_REGEX,
      this.charge && this.charge.emailAddress ? this.charge.emailAddress : "unknown"
    );
  }

  @computed
  get doesNotHaveEmail() {
    return !this.charge?.emailAddress;
  }

  @action.bound
  closeEmailModal() {
    this.showModal = false;
  }

  @bound
  protected onEmailUpdated(data: ReturnType<typeof emailUpdated>) {
    this.setEmailAddress(data.payload.emailAddress);
    if (this.emailInputViewModel) {
      this.emailInputViewModel = undefined;
    }
  }

  @bound
  protected async putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>) {
    if (data.payload.triggerer !== "contactChange") {
      this.emailInputViewModel = undefined;
      return;
    }

    this.clearAfterAsyncAction();

    if (!this.charge) {
      return;
    }

    this.charge.notificationPreferences.smsNotificationEnabled = data.payload.notificationPreferences?.smsNotificationEnabled;
    this.charge.notificationPreferences.emailNotificationEnabled = data.payload.notificationPreferences?.emailNotificationEnabled;
    this.charge.notificationPreferences._emailAddressNotification =
      data.payload.notificationPreferences?._emailAddressNotification;
    this.charge.notificationPreferences._emailConfirmation = data.payload.emailAddress;
    this.charge.emailAddress = data.payload.emailAddress;
    this.charge.notificationPreferences.phoneNumber = data.payload.notificationPreferences?.phoneNumber;
    if (data.payload.authCode) this.charge.authCode = data.payload.authCode;
  }

  @action.bound
  @watchBusy
  async sendPaymentConfirmationEmail() {
    // If the user doesn't have an email address, we need to display a modal window first.
    this.checkRequiredEmail = true;
    if (this.charge && !this.charge.emailAddress && !this.emailModalOpened && this.checkRequiredEmail) {
      this.openChargePaymentConfirmationModal();
      return;
    }

    // Once the user fills out their email (or has had it already, we submit it).
    this.success = false;
    this.errorMessage = undefined;

    if (!this.validate()) return;

    // Set action that will be called after recaptcha has validated
    this.recaptchaAction = this.actionSendPaymentConfirmationEmail;
    this.recatpchaFailed = () => {
      this.eventBus.publish(
        sendChargePaymentConfirmationEmailFailure({ type: SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE })
      );
    };

    try {
      await this.recaptchaRef?.current?.execute().then(() => fixReCaptchaChallengePosition());
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("recaptcha", "execute");
        Sentry.captureException(error);
      });
      this.eventBus.publish(
        sendChargePaymentConfirmationEmailFailure({ type: SEND_CHARGE_PAYMENT_CONFIRMATION_EMAIL_REQUEST_FAILURE })
      );
    }
  }

  @action.bound
  @watchBusy
  async actionSendPaymentConfirmationEmail() {
    await this.chargeManagementService.sendChargePaymentConfirmationEmail(
      this.emailModalOpened ? this.charge?.emailAddress : undefined,
      this.recaptchaToken
    );
  }

  @action.bound
  @watchBusy
  async downloadPaymentConfirmation() {
    this.success = false;
    this.errorMessage = undefined;
    this.checkRequiredEmail = false;
    try {
      // Set action that will be called after recaptcha has validated
      this.recaptchaAction = this.actionDownloadPaymentConfirmation;
      this.recatpchaFailed = () => {
        this.eventBus.publish(downloadPaymentConfirmationFailure({ type: DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE }));
      };

      await this.recaptchaRef?.current?.execute().then(() => fixReCaptchaChallengePosition());
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("recaptcha", "execute");
        Sentry.captureException(error);
      });
      this.eventBus.publish(downloadPaymentConfirmationFailure({ type: DOWNLOAD_PAYMENT_CONFIRMATION_REQUEST_FAILURE }));
    }
  }

  @action.bound
  @watchBusy
  async actionDownloadPaymentConfirmation() {
    await saveFileFromURL(
      () => this.chargeManagementService.downloadPaymentConfirmation(this.recaptchaToken),
      "Charge management",
      {
        charge: this.charge,
      }
    );
  }

  @action.bound
  @watchBusy
  async onRecaptchaVerify(recaptchaToken: string) {
    await this.recaptchaRef?.current?.reset();

    if (this.charge && !this.charge.emailAddress && this.checkRequiredEmail) {
      const request = { emailAddress: this.notificationPreferences.emailAddress } as NotificationPreferences;
      await this.chargeManagementService.editContact(request, "contactChange", recaptchaToken);
    }

    if (recaptchaToken) {
      this.recaptchaToken = recaptchaToken;
      if (this.recaptchaAction) {
        this.recaptchaAction();
        this.recaptchaAction = undefined;
        this.recatpchaFailed = undefined;
      }
    } else {
      if (this.recatpchaFailed) {
        this.recatpchaFailed();
        this.recaptchaAction = undefined;
        this.recatpchaFailed = undefined;
      }
    }
  }
}
